import { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Skeleton
} from '@mui/material';

import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

/** Toolbar height in px for layout calculations */
const TOOLBAR_HEIGHT = 48;

/**
 * Visor de PDF avanzado con controles de zoom, navegación y recorte automático
 * del espacio en blanco derecho (para PDFs landscape con contenido solo a la izquierda).
 *
 * @param {Object} props
 * @param {string} props.file - URL o blob del PDF a mostrar
 * @param {string} [props.fileName] - Nombre del archivo para la descarga
 * @param {number} [props.cropRatio] - Proporción del ancho a mostrar (0-1). Default 0.5 = mitad izquierda
 */
export const PdfViewerAdvanced = ({ file, fileName = 'documento.pdf', cropRatio = 0.5 }) => {
  const containerRef = useRef();

  const [numPages, setNumPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(true);
  const [containerWidth, setContainerWidth] = useState(300);

  // Responsive: actualizar ancho del contenedor al redimensionar
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const onLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  // Zoom controls
  const handleZoomIn = () => setScale((s) => Math.min(s + 0.2, 3));
  const handleZoomOut = () => setScale((s) => Math.max(s - 0.2, 0.6));

  // Navegación entre páginas
  const nextPage = () => setPageNumber((p) => Math.min(p + 1, numPages));
  const prevPage = () => setPageNumber((p) => Math.max(p - 1, 1));

  // Descargar PDF
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = file;
    link.download = fileName;
    link.click();
  };

  // El PDF se renderiza al ancho completo dividido por cropRatio, y el contenedor
  // recorta la mitad derecha con overflow:hidden mostrando solo la porción con contenido.
  const PDF_PADDING = 16; // 8px por lado
  const renderWidth = ((containerWidth - PDF_PADDING) / cropRatio) * scale;

  return (
    <Box
      ref={containerRef}
      sx={{
        height: 'calc(100dvh - 48px)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#111',
        overflow: 'hidden',
        borderRadius: '16px',
        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.08)',
      }}
    >
      {/* Toolbar */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={1.5}
        sx={{
          height: TOOLBAR_HEIGHT,
          minHeight: TOOLBAR_HEIGHT,
          bgcolor: '#2c2c2e',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.6)',
            fontSize: '0.78rem',
            fontWeight: 500,
          }}
        >
          {pageNumber} / {numPages}
        </Typography>

        <Box>
          <IconButton onClick={handleZoomOut} size="small" sx={{ color: '#fff' }}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>

          <IconButton onClick={handleZoomIn} size="small" sx={{ color: '#fff' }}>
            <ZoomInIcon fontSize="small" />
          </IconButton>

          <IconButton onClick={handleDownload} size="small" sx={{ color: '#fff' }}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Área del PDF — ocupa todo el alto restante */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          bgcolor: '#1c1c1e',
          px: 1, // 8px de separación a cada lado
        }}
      >
        {/* Skeleton loader */}
        {loading && (
          <Skeleton
            variant="rectangular"
            width="90%"
            height="80%"
            sx={{
              borderRadius: 2,
              position: 'absolute',
              top: '10%',
              left: '5%',
            }}
          />
        )}

        {/*
         * Contenedor de recorte: el ancho visible es el del contenedor (100%),
         * pero el PDF se renderiza al doble (renderWidth = containerWidth / cropRatio).
         * overflow:hidden oculta la mitad derecha en blanco.
         */}
        <Box
          sx={{
            width: (containerWidth - PDF_PADDING) * scale,
            maxWidth: '100%',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <Document file={file} onLoadSuccess={onLoadSuccess}>
            <Page
              pageNumber={pageNumber}
              width={renderWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        </Box>

        {/* Navegación flotante */}
        {pageNumber > 1 && (
          <IconButton
            onClick={prevPage}
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.5)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
        )}

        {pageNumber < numPages && (
          <IconButton
            onClick={nextPage}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.5)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
